import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Grid, TextField, Typography } from '@mui/material';

const normalizeWhitespace = (value) => String(value || '').trim().replace(/\s+/g, ' ');

const normalizePart = (value) => normalizeWhitespace(String(value || '').replace(/,+/g, ' '));

const parseLocation = (value) => {
  const raw = normalizeWhitespace(value);
  if (!raw) {
    return { street: '', municipality: '', province: '', postalCode: '', country: '' };
  }

  const parts = raw
    .split(',')
    .map((p) => normalizeWhitespace(p))
    .filter(Boolean);

  const splitAt = Math.max(parts.length - 3, 1);
  const street = parts.slice(0, splitAt).join(', ');
  const municipality = parts[splitAt] || '';
  const provincePostal = parts[splitAt + 1] || '';
  const country = parts[splitAt + 2] || '';

  let province = provincePostal;
  let postalCode = '';
  const m = provincePostal.match(/^(.*?)(?:\s+)?(\d{3,6})$/);
  if (m) {
    province = normalizePart(m[1]);
    postalCode = normalizePart(m[2]);
  }

  return {
    street,
    municipality,
    province,
    postalCode,
    country
  };
};

const buildLocation = (parts) => {
  const street = normalizePart(parts.street);
  const municipality = normalizePart(parts.municipality);
  const province = normalizePart(parts.province);
  const postalCode = normalizePart(parts.postalCode);
  const country = normalizePart(parts.country);

  const provincePostal = normalizePart(`${province}${postalCode ? ` ${postalCode}` : ''}`);

  return [street, municipality, provincePostal, country].filter(Boolean).join(', ');
};

const StructuredLocationFields = ({
  value,
  onChange,
  disabled = false,
  required = false,
  margin = 'dense',
  title = 'Address',
  showTitle = true,
  showCombinedField = true
}) => {
  const lastExternalValueRef = useRef(undefined);
  const lastEmittedValueRef = useRef(undefined);
  const [parts, setParts] = useState(() => parseLocation(value));

  useEffect(() => {
    const v = typeof value === 'string' ? value : '';
    if (lastEmittedValueRef.current === v) {
      lastExternalValueRef.current = v;
      return;
    }
    if (lastExternalValueRef.current === v) return;
    lastExternalValueRef.current = v;
    setParts(parseLocation(v));
  }, [value]);

  const combined = useMemo(() => buildLocation(parts), [parts]);

  const setAndEmit = (nextParts) => {
    const nextCombined = buildLocation(nextParts);
    setParts(nextParts);
    if (typeof onChange === 'function') {
      lastEmittedValueRef.current = nextCombined;
      onChange(nextCombined);
    }
  };

  const handlePartChange = (key) => (e) => {
    const nextValue = String(e?.target?.value || '').replace(/,+/g, ' ');
    setAndEmit({ ...parts, [key]: nextValue });
  };

  const handleCombinedChange = (e) => {
    const nextCombined = e?.target?.value;
    if (typeof onChange === 'function') {
      lastEmittedValueRef.current = nextCombined;
      onChange(nextCombined);
    }
    setParts(parseLocation(nextCombined));
  };

  return (
    <Box sx={{ mt: 1 }}>
      {showTitle && (
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          {title}
        </Typography>
      )}
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TextField
            margin={margin}
            label="Street / Address"
            fullWidth
            value={parts.street}
            onChange={handlePartChange('street')}
            disabled={disabled}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            margin={margin}
            label="Municipality"
            fullWidth
            value={parts.municipality}
            onChange={handlePartChange('municipality')}
            disabled={disabled}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            margin={margin}
            label="Province / State"
            fullWidth
            value={parts.province}
            onChange={handlePartChange('province')}
            disabled={disabled}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            margin={margin}
            label="Postal Code"
            fullWidth
            value={parts.postalCode}
            onChange={handlePartChange('postalCode')}
            disabled={disabled}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            margin={margin}
            label="Country"
            fullWidth
            value={parts.country}
            onChange={handlePartChange('country')}
            disabled={disabled}
          />
        </Grid>
        {showCombinedField && (
          <Grid item xs={12}>
            <TextField
              margin={margin}
              label="Location (combined)"
              fullWidth
              value={combined}
              onChange={handleCombinedChange}
              disabled={disabled}
              required={required}
            />
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default StructuredLocationFields;
